import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, confirmSignIn, resetPassword, confirmResetPassword } from '../lib/auth.js';
import { useAuth } from '../context/AuthContext.js';
import { theme } from '../theme.js';
import { Button } from '../components/Button.js';
import { Logo } from '../components/Logo.js';
import { passwordErrorMessage, PASSWORD_RULES } from '../lib/authErrors.js';

export function LoginPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  // Where to go once signed in: only an invite link (accepting it needs a sign-in).
  const nextParam = searchParams.get('next') ?? '';
  const next = /^\/claim\/[\w-]+$/.test(nextParam) ? nextParam : '/';
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  // Forgot password: ask for the email, then the emailed code + a new password.
  const [resetStep, setResetStep] = useState<'email' | 'code' | null>(null);
  const [resetCode, setResetCode] = useState('');
  const [resetDestination, setResetDestination] = useState('');

  /** Finish sign-in, or move to whichever challenge Cognito asks for next. */
  async function continueSignIn(result: Awaited<ReturnType<typeof signIn>>): Promise<void> {
    if (result.isSignedIn) {
      await refresh();
      nav(next, { replace: true });
      return;
    }
    switch (result.nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setNeedsNewPassword(true);
        return;
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        setNeedsNewPassword(false);
        setNeedsTotp(true);
        return;
      default:
        setError('Additional sign-in steps are required — please contact an admin.');
    }
  }

  async function handleTotp(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await continueSignIn(await confirmSignIn({ challengeResponse: totpCode.trim() }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That code didn’t work');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await continueSignIn(await signIn({ username: email, password }));
    } catch (err: unknown) {
      // Amplify throws this if a valid session already exists (e.g. a stale
      // tab, or our own AuthContext not having picked up an earlier sign-in
      // yet) — that's not a real failure, just get the context in sync.
      if (err instanceof Error && err.name === 'UserAlreadyAuthenticatedException') {
        await refresh();
        nav(next, { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  }

  function startReset() {
    setError('');
    setNotice('');
    setResetCode('');
    setNewPassword('');
    setResetStep('email');
  }

  function backToSignIn(message = '') {
    setError('');
    setNotice(message);
    setPassword('');
    setResetStep(null);
  }

  async function handleResetRequest(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { nextStep } = await resetPassword({ username: email.trim() });
      if (nextStep.resetPasswordStep === 'DONE') {
        backToSignIn('Your password has been reset — sign in.');
        return;
      }
      setResetDestination(nextStep.codeDeliveryDetails.destination ?? '');
      setResetStep('code');
    } catch (err: unknown) {
      setError(resetErrorMessage(err, 'Could not send a reset code'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResetConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email.trim(), confirmationCode: resetCode.trim(), newPassword,
      });
      setNewPassword('');
      backToSignIn('Password changed — sign in with your new password.');
    } catch (err: unknown) {
      setError(resetErrorMessage(err, 'Could not change your password'));
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await continueSignIn(await confirmSignIn({ challengeResponse: newPassword }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not set new password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: theme.bg, padding: 24,
    }}>
      {/* auto margins centre the form in the space above the footer */}
      <div style={{ width: '100%', maxWidth: 360, marginBlock: 'auto' }}>
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <Logo size={28} stacked />
          <div style={{ fontSize: 14, color: theme.textMuted, marginTop: 6 }}>
            Checks and rosters for your unit
          </div>
        </div>

        {resetStep === 'email' ? (
          <form onSubmit={handleResetRequest} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: theme.textMuted }}>
              Enter the email you sign in with and we’ll email you a code to reset your password.
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: theme.textDim }}>Email</span>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="email" autoFocus
                style={inputStyle}
              />
            </label>

            {error && <ErrorBox>{error}</ErrorBox>}

            <Button type="submit" fullWidth size="lg" loading={loading}>
              Send code
            </Button>
            <button type="button" onClick={() => backToSignIn()} style={linkBtn}>
              Back to sign in
            </button>
          </form>
        ) : resetStep === 'code' ? (
          <form onSubmit={handleResetConfirm} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: theme.textMuted }}>
              If {email.trim()} has an account, a code is on its way{resetDestination ? ` to ${resetDestination}` : ''}.
              Enter it below with a new password. It can take a minute — check your spam folder too.
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: theme.textDim }}>Code</span>
              <input
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
                required autoFocus
                style={{ ...inputStyle, fontSize: 20, letterSpacing: 6, textAlign: 'center' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: theme.textDim }}>New password</span>
              <input
                type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                required autoComplete="new-password" minLength={10}
                style={inputStyle}
              />
              <span style={{ fontSize: 12, color: theme.textMuted }}>
                {PASSWORD_RULES}
              </span>
            </label>

            {error && <ErrorBox>{error}</ErrorBox>}

            <Button type="submit" fullWidth size="lg" loading={loading} disabled={!resetCode}>
              Change password
            </Button>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={startReset} style={linkBtn}>
                Send a new code
              </button>
              <button type="button" onClick={() => backToSignIn()} style={linkBtn}>
                Back to sign in
              </button>
            </div>
          </form>
        ) : needsTotp ? (
          <form onSubmit={handleTotp} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: theme.textMuted }}>
              Enter the 6-digit code from your authenticator app.
            </div>

            <input
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
              aria-label="6-digit code" required autoFocus
              style={{ ...inputStyle, fontSize: 20, letterSpacing: 6, textAlign: 'center' }}
            />

            {error && (
              <div style={{
                fontSize: 13, color: theme.status.danger.fg,
                background: theme.status.danger.bg,
                padding: '10px 14px', borderRadius: 8,
              }}>{error}</div>
            )}

            <Button type="submit" fullWidth size="lg" loading={loading} disabled={totpCode.length !== 6}>
              Verify
            </Button>
          </form>
        ) : needsNewPassword ? (
          <form onSubmit={handleNewPassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: theme.textMuted }}>
              This is your first sign-in — choose a new password to continue.
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: theme.textDim }}>New password</span>
              <input
                type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                required autoComplete="new-password" minLength={10}
                style={inputStyle}
              />
            </label>

            {error && (
              <div style={{
                fontSize: 13, color: theme.status.danger.fg,
                background: theme.status.danger.bg,
                padding: '10px 14px', borderRadius: 8,
              }}>{error}</div>
            )}

            <Button type="submit" fullWidth size="lg" loading={loading}>
              Set password
            </Button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {notice && (
              <div style={{
                fontSize: 13, color: theme.status.ok.fg,
                background: theme.status.ok.bg,
                padding: '10px 14px', borderRadius: 8,
              }}>{notice}</div>
            )}

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: theme.textDim }}>Email</span>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="email"
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: theme.textDim }}>Password</span>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required autoComplete="current-password"
                style={inputStyle}
              />
            </label>

            {error && (
              <div style={{
                fontSize: 13, color: theme.status.danger.fg,
                background: theme.status.danger.bg,
                padding: '10px 14px', borderRadius: 8,
              }}>{error}</div>
            )}

            <Button type="submit" fullWidth size="lg" loading={loading}>
              Sign in
            </Button>
            <button type="button" onClick={startReset} style={linkBtn}>
              Forgot password?
            </button>
          </form>
        )}
      </div>

      <footer style={{
        marginTop: 32, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 16px',
        fontSize: 13, paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        <Link to="/" style={footerLink}>What is MusterTap?</Link>
        <Link to="/privacy" style={footerLink}>Privacy</Link>
        <a href="mailto:enquiries@mustertap.com" style={footerLink}>Contact us</a>
      </footer>
    </div>
  );
}

const footerLink: React.CSSProperties = { color: theme.textMuted, textDecoration: 'none' };

const inputStyle: React.CSSProperties = {
  height: 44, padding: '0 14px',
  border: `1px solid ${theme.border}`, borderRadius: 10,
  background: theme.surface, color: theme.text,
  fontFamily: theme.font, fontSize: 15, outline: 'none',
  width: '100%',
};

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 13, color: theme.status.danger.fg,
      background: theme.status.danger.bg,
      padding: '10px 14px', borderRadius: 8,
    }}>{children}</div>
  );
}

const ASK_ADMIN = 'This account can’t reset its password yet — usually because the first sign-in with the temporary password never happened. Ask a unit admin to use “Reset sign-in” on the Members page; they’ll give you a new temporary password.';

/** Readable messages for Cognito's forgot-password errors. */
function resetErrorMessage(err: unknown, fallback: string): string {
  // e.g. "User password cannot be reset in the current state" (FORCE_CHANGE_PASSWORD).
  return passwordErrorMessage(err, fallback, { NotAuthorizedException: ASK_ADMIN, InvalidParameterException: ASK_ADMIN });
}

const linkBtn: React.CSSProperties = {
  alignSelf: 'center', padding: '4px 0', background: 'none', border: 'none',
  cursor: 'pointer', fontFamily: theme.font, fontSize: 13, color: theme.textDim,
  textDecoration: 'underline',
};
